'use client';
import { useEffect, useState } from 'react';
import { AppNav } from '@/components/AppNav';
import { useResource } from '@/lib/use-resource';
import { RUNGS, type RungId } from '@/lib/playbook/state';
import { ACTION_TAGS, type ActionTag, type LogEntry } from '@/lib/playbook/log-types';

// Drawdown playbook. Status strip + rung map + trail of state transitions.
// Per-holding action table is still stubbed (Phase 2 — needs rankStrategies
// from lib/hedge-structures.ts called per holding with synthetic dropped-spot
// context to render concrete @WATCH/@REDUCE/@HALT trades).

interface PlaybookCurrent {
  asOf: string;
  state: RungId;
  bookMinusSpyYtdPp: number | null;
  ddFromPeakPct: number | null;
  avgComposite: number | null;
  triggers: Array<{ rung: RungId; reason: 'dd' | 'composite' }>;
}

interface PlaybookResponse {
  current: PlaybookCurrent;
  log: LogEntry[];
}

const RUNG_TONE: Record<RungId, string> = {
  armed: 'text-emerald-300 border-emerald-300/40 bg-emerald-300/5',
  watch: 'text-amber-300 border-amber-300/40 bg-amber-300/5',
  reduce: 'text-orange-300 border-orange-300/40 bg-orange-300/5',
  halt: 'text-rose-300 border-rose-300/40 bg-rose-300/5',
};

const STUB_HOLDINGS = [
  { symbol: 'AMZN', composite: 2.4, weight: 13.4, watch: 'Trim 1pp', reduce: 'Covered call 30d 30Δ', halt: 'Exit half' },
  { symbol: 'NVDA', composite: 2.8, weight: 9.1, watch: 'Collar 30Δ/25Δ', reduce: 'Tighten collar', halt: 'Exit' },
  { symbol: 'MA', composite: 3.1, weight: 8.6, watch: 'Hold', reduce: 'Covered call', halt: 'Trim half' },
  { symbol: 'MU', composite: 2.2, weight: 7.4, watch: 'Hold', reduce: 'Collar', halt: 'Exit' },
  { symbol: 'TSM', composite: 3.4, weight: 6.9, watch: 'Hold', reduce: 'Hold', halt: 'Trim half' },
  { symbol: 'TLN', composite: 1.9, weight: 6.1, watch: 'Trim 0.5pp', reduce: 'Exit half', halt: 'Exit full' },
  { symbol: 'VST', composite: 2.7, weight: 5.8, watch: 'Hold', reduce: 'Collar', halt: 'Exit' },
];

function fmtPp(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}pp`;
}
function fmtPct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}
function fmtComp(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PlaybookPage() {
  const { data, error, refetch } = useResource<PlaybookResponse>('/api/playbook');
  const current = data?.current ?? null;
  const log = data?.log ?? [];
  const state: RungId = current?.state ?? 'armed';
  const currentRung = RUNGS.find(r => r.id === state) ?? RUNGS[0];
  const stateTone = RUNG_TONE[state];

  return (
    <div className="min-h-screen text-[#e2e8f0] pb-12">
      <AppNav />
      <div className="max-w-6xl mx-auto px-4 pt-2">
        <div className="sticky top-[36px] z-40 bg-[#0a0e14] -mx-4 px-4 pt-1 pb-2 mb-3 border-b border-[#1e293b] shadow-[0_4px_8px_-4px_rgba(0,0,0,0.4)]">
          <div className={`inline-flex items-center gap-2 px-2 py-0.5 rounded border ${stateTone} mb-2`}>
            <span className="w-1 h-1 rounded-full bg-current" aria-hidden />
            <span className="text-[9px] uppercase tracking-[0.15em] font-semibold">State: {currentRung.label}</span>
          </div>
          <h1 className="text-2xl font-semibold">Playbook</h1>
          <p className="text-[11px] text-[#94a3b8] leading-snug max-w-4xl mt-1">
            Drawdown helper. Live diagnostic of where the book sits on the rung ladder, with the action that would be suggested at each rung. You decide; the page just shows the state. Book − SPY YTD is informational — the rungs key off drawdown from peak and average composite.
          </p>
        </div>

        {error && (
          <div className="rounded border border-rose-400/40 bg-rose-400/5 text-rose-300 text-[11px] px-3 py-2 mb-3">
            Could not load playbook data: {error}
          </div>
        )}

        <section className="grid grid-cols-4 gap-3 mb-6">
          <Cell label="Book − SPY YTD" value={fmtPp(current?.bookMinusSpyYtdPp ?? null)} tone="info" hint="informational only" />
          <Cell
            label="DD from peak"
            value={fmtPct(current?.ddFromPeakPct ?? null)}
            tone={ddTone(current?.ddFromPeakPct ?? null)}
          />
          <Cell
            label="Avg composite"
            value={fmtComp(current?.avgComposite ?? null)}
            tone={compTone(current?.avgComposite ?? null)}
          />
          <Cell label="State" value={currentRung.label} tone="neutral" boldTone={stateTone} />
        </section>

        <h2 className="text-[var(--gold)] text-[18px] font-semibold mb-3">Rung map</h2>
        <section className="grid grid-cols-3 gap-3 mb-8">
          {RUNGS.filter(r => r.id !== 'armed').map(r => (
            <div key={r.id} className={`rounded-lg border ${RUNG_TONE[r.id]} px-4 py-3 ${state === r.id ? 'ring-2 ring-current' : ''}`}>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2">{r.label}</div>
              <div className="text-[10px] text-[#94a3b8] mb-1">Activates when any:</div>
              <ul className="text-[11px] tabular-nums mb-3 space-y-0.5">
                <li>DD from peak &lt; {r.ddPctThreshold}%</li>
                <li>Avg composite &lt; {r.avgCompositeThreshold?.toFixed(1)}</li>
              </ul>
              <div className="text-[10px] text-[#94a3b8] mb-1">Suggested action:</div>
              <ul className="text-[11.5px] leading-snug list-disc list-inside space-y-0.5">
                {r.actions.map(a => <li key={a}>{a}</li>)}
              </ul>
            </div>
          ))}
        </section>

        <h2 className="text-[var(--gold)] text-[18px] font-semibold mb-3">Trail</h2>
        <p className="text-[11px] text-[#94a3b8] leading-snug mb-3">
          Every state change is logged. Red dot = open. Click the dot to mark handled, pick an action to tag what you did, leave a note for next time. Entries auto-handle (asterisk badge) when you save a portfolio change after the trigger fired.
        </p>
        <TrailSection log={log} onChanged={refetch} />

        <h2 className="text-[var(--gold)] text-[18px] font-semibold mb-3">Per-holding action</h2>
        <p className="text-[11px] text-[#94a3b8] leading-snug mb-3">
          What the playbook would suggest for each position at each rung. Hedge-Lab&apos;s strategy ranker (lib/hedge-structures.ts) will source these once wired (Phase 2). Numbers below are stubbed.
        </p>
        <section className="rounded-lg border border-[#1e293b] overflow-hidden mb-6">
          <table className="w-full text-[12px] tabular-nums">
            <thead className="bg-[#0f172a] text-[#94a3b8]">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Symbol</th>
                <th className="px-3 py-2 font-semibold">Wt</th>
                <th className="px-3 py-2 font-semibold">Comp</th>
                <th className="px-3 py-2 font-semibold text-amber-300">@ WATCH</th>
                <th className="px-3 py-2 font-semibold text-orange-300">@ REDUCE</th>
                <th className="px-3 py-2 font-semibold text-rose-300">@ HALT</th>
              </tr>
            </thead>
            <tbody>
              {STUB_HOLDINGS.map(h => (
                <tr key={h.symbol} className="border-t border-[#1e293b]">
                  <td className="px-3 py-1.5 font-semibold">{h.symbol}</td>
                  <td className="px-3 py-1.5">{h.weight.toFixed(1)}%</td>
                  <td className="px-3 py-1.5">{h.composite.toFixed(1)}</td>
                  <td className="px-3 py-1.5 text-amber-200/80">{h.watch}</td>
                  <td className="px-3 py-1.5 text-orange-200/80">{h.reduce}</td>
                  <td className="px-3 py-1.5 text-rose-200/80">{h.halt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <p className="text-[10px] text-[#64748b] leading-snug">
          As of {current?.asOf ?? '—'}. Status strip live: weight-sum YTD vs SPY, DD from peak from the current-book backtest, avg composite over equity weights. Per-holding action table is stubbed pending Phase 2 (live rankStrategies per name).
        </p>
      </div>
    </div>
  );
}

type Tone = 'pos' | 'neg' | 'neutral' | 'info';

function ddTone(ddPct: number | null): Tone {
  if (ddPct == null) return 'neutral';
  if (ddPct <= -10) return 'neg';
  if (ddPct >= -5) return 'pos';
  return 'neutral';
}
function compTone(avg: number | null): Tone {
  if (avg == null) return 'neutral';
  if (avg < 2.0) return 'neg';
  if (avg >= 2.5) return 'pos';
  return 'neutral';
}

function Cell({ label, value, tone, boldTone, hint }: { label: string; value: string; tone: Tone; boldTone?: string; hint?: string }) {
  const valueColor = tone === 'pos' ? 'text-emerald-300' : tone === 'neg' ? 'text-rose-300' : tone === 'info' ? 'text-white/80' : 'text-white';
  return (
    <div className={`rounded-lg border border-[#1e293b] bg-[#0f172a] px-4 py-3 ${boldTone ?? ''}`}>
      <div className="text-[9px] uppercase tracking-[0.15em] text-[#94a3b8] mb-1">{label}</div>
      <div className={`text-[22px] font-semibold tabular-nums ${boldTone ? '' : valueColor}`}>{value}</div>
      {hint && <div className="text-[9px] text-[#64748b] mt-0.5">{hint}</div>}
    </div>
  );
}

function TrailSection({ log, onChanged }: { log: LogEntry[]; onChanged: () => void }) {
  const reversed = [...log].reverse();
  if (reversed.length === 0) {
    return (
      <div className="rounded-lg border border-[#1e293b] bg-[#0f172a] px-4 py-6 text-[11px] text-[#64748b] mb-6">
        No state transitions yet. The first entry appears the moment WATCH, REDUCE, or HALT fires.
      </div>
    );
  }
  return (
    <section className="flex flex-col gap-2 mb-8">
      {reversed.map(e => <TrailCard key={e.id} entry={e} onChanged={onChanged} />)}
    </section>
  );
}

async function patchEntryRequest(id: string, body: Record<string, unknown>): Promise<void> {
  await fetch('/api/playbook', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ id, ...body }),
  });
}

function TrailCard({ entry, onChanged }: { entry: LogEntry; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const tone = RUNG_TONE[entry.to];
  async function toggleHandled() {
    if (busy) return;
    setBusy(true);
    try {
      await patchEntryRequest(entry.id, { handled: !entry.handled });
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function setAction(next: ActionTag | null) {
    setBusy(true);
    try {
      await patchEntryRequest(entry.id, { action: next });
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function setNote(next: string | null) {
    setBusy(true);
    try {
      await patchEntryRequest(entry.id, { note: next });
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  const triggerText = entry.triggers.length === 0
    ? '—'
    : entry.triggers.map(t => `${t.rung}/${t.reason === 'dd' ? 'DD' : 'comp'}`).join(' · ');
  const handledLabel = entry.handled && entry.handledAt
    ? `handled ${fmtTime(entry.handledAt)}${entry.autoHandled ? ' ✱ auto' : ''}`
    : '';
  return (
    <article className={`rounded-lg border ${entry.handled ? 'border-[#1e293b] bg-[#0f172a]' : 'border-rose-400/30 bg-[#1a1018]'} px-4 py-3`}>
      <div className="flex items-center gap-3 mb-2">
        <button
          type="button"
          onClick={toggleHandled}
          disabled={busy}
          aria-label={entry.handled ? 'Mark unhandled' : 'Mark handled'}
          className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:ring-2 hover:ring-white/30 disabled:opacity-50"
          title={entry.handled ? `Handled ${entry.handledAt ? fmtTime(entry.handledAt) : ''}` : 'Click to mark handled'}
        >
          <span className={`w-3 h-3 rounded-full ${entry.handled ? 'bg-emerald-400' : 'bg-rose-400'}`} />
        </button>
        <span className="text-[#64748b] text-[11px]">{entry.from.toUpperCase()}</span>
        <span className="text-[#475569] text-[11px]">→</span>
        <span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-[0.1em] ${tone}`}>{entry.to}</span>
        <span className="text-[10px] text-[#94a3b8] ml-auto tabular-nums">{fmtTime(entry.ts)}</span>
      </div>
      <div className="grid grid-cols-4 gap-3 text-[11px] mb-2">
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#94a3b8]">Trigger</div>
          <div className="text-[#cbd5e1]">{triggerText}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#94a3b8]">DD at trigger</div>
          <div className="tabular-nums">{fmtPct(entry.snapshot.ddFromPeakPct)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#94a3b8]">Avg comp at trigger</div>
          <div className="tabular-nums">{fmtComp(entry.snapshot.avgComposite)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#94a3b8]">Status</div>
          <div className={`text-[10px] ${entry.handled ? 'text-emerald-400' : 'text-rose-300'}`}>{entry.handled ? handledLabel : 'open'}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#94a3b8]">
          Action
          <select
            value={entry.action ?? ''}
            disabled={busy}
            onChange={ev => setAction(ev.target.value ? (ev.target.value as ActionTag) : null)}
            className="bg-[#0a0e14] border border-[#1e293b] rounded px-2 py-1 text-[11px] text-[#e2e8f0] normal-case tracking-normal"
          >
            <option value="">—</option>
            {ACTION_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <NoteInput
          initial={entry.note ?? ''}
          disabled={busy}
          onSave={(value) => { void setNote(value ? value : null); }}
        />
      </div>
    </article>
  );
}

function NoteInput({ initial, disabled, onSave }: { initial: string; disabled: boolean; onSave: (value: string) => void }) {
  const [value, setValue] = useState(initial);
  // Sync if the upstream entry got refreshed (e.g. another tab).
  useEffect(() => { setValue(initial); }, [initial]);
  return (
    <input
      type="text"
      value={value}
      placeholder="note (e.g. trimmed 200 sh MA at $543)"
      disabled={disabled}
      onChange={ev => setValue(ev.target.value)}
      onBlur={() => { if (value !== initial) onSave(value); }}
      className="flex-1 min-w-[280px] bg-[#0a0e14] border border-[#1e293b] rounded px-2 py-1 text-[11px] text-[#e2e8f0] placeholder:text-[#475569] focus:outline-none focus:border-[#475569]"
    />
  );
}
